import { makeStyles } from "@mui/styles";
import { Button } from "components/Button/Button";
import { GroupAutocompleteOptions } from "components/Form/components/Autocomplete/Autocomplete";
import { CancelButton } from "components/Form/components/CancelButton/CancelButton";
import { Popover } from "components/Popover/Popover";
import { MoveToLocationAutocomplete } from "containers/Resources/components/CommonActions/MoveToLocation/MoveToLocationAutocomplete";
import { ReactNode } from "react";
import "./MoveToLocation.scss";

interface LocationPickerPopoverProps {
    /** Whether the dialog is open. */
    isOpen: boolean;
    /** Bold header line (e.g. "Move selected resource to:", "Upload file to:"). */
    title: string;
    /** Label of the primary confirm button (e.g. "Move", "Upload"). */
    confirmLabel: string;
    /** Disable the confirm button (e.g. no location selected / same location). */
    confirmDisabled?: boolean;
    /** Show the confirm button in its loading state. */
    isLoading?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
    /** Location picker wiring (forwarded to MoveToLocationAutocomplete). */
    name: string;
    value?: string;
    changeValue: (value: string) => void;
    options: { group: GroupAutocompleteOptions };
    recordLocationId?: string;
    /** Optional extra content rendered between the picker and the buttons (e.g. warnings). */
    children?: ReactNode;
}

/**
 * The shared "choose a container (project/bundle/workspace)" dialog shell.
 *
 * This is the exact pop-up used by the Move action — the custom `Popover`
 * (rendered to `document.body` with `overflow: visible`, so the location
 * dropdown can spill outside the dialog) plus the shared `MoveToLocation.scss`
 * styling (compact 24px padding, bold in-body header, Cancel-left / confirm-right
 * button row). Both the record Move action and the create-menu file upload use
 * it, so the two dialogs stay pixel-identical from a single source of truth.
 */
export const LocationPickerPopover = ({
    isOpen,
    title,
    confirmLabel,
    confirmDisabled,
    isLoading,
    onConfirm,
    onCancel,
    name,
    value,
    changeValue,
    options,
    recordLocationId,
    children,
}: LocationPickerPopoverProps) => {
    const rootStyle = {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    };
    const paperStyle = {
        maxHeight: "600px",
        overflow: "visible",
    };
    const useStyles = makeStyles(() => ({
        rootStyle,
        paperStyle,
    }));
    const classes = useStyles();

    return (
        <Popover
            isOpen={isOpen}
            classes={{ root: classes.rootStyle, paper: classes.paperStyle }}
            rootClassName="moveToLocation__popover"
            contentClassName="moveToLocation__content"
            anchorReference="none"
        >
            <div className="moveToLocation__header">{title}</div>
            <MoveToLocationAutocomplete
                name={name}
                changeValue={changeValue}
                value={value}
                options={options}
                recordLocationId={recordLocationId}
            />
            {children}
            <div className="button-container">
                <CancelButton onClick={onCancel} />
                <Button
                    className="confirm-button"
                    variation="primary"
                    disabled={confirmDisabled}
                    loading={isLoading}
                    onClick={onConfirm}
                    label={confirmLabel}
                />
            </div>
        </Popover>
    );
};
